/**
 * PengestromMap — ekte (forenklet) Norge-kart i ren inline-SVG.
 *
 * Tegner Norges fastlands-omriss fra datafilen `norway.geo.json` (GeoJSON, ikke
 * en lib), projiserer investor-koordinater og prosjektadressen til SVG-rommet med
 * samme projeksjon, og legger glødende noder (radius ∝ beløp) + buede flyt-linjer
 * fra hver investor-by inn til prosjektnoden — i amber-glød-stilen fra mockupen
 * (PALETT.md). Flyt-linjene animeres subtilt; animasjonen slås av ved
 * `prefers-reduced-motion: reduce`.
 *
 * Selvstendig presentasjons-komponent: den tar ferdige lat/lon inn via props
 * (integrator/parent geokoder via worker + ev. by-fallback før kall).
 */

import norwayGeo from "./norway.geo.json";
import { formatAmount } from "./api/format";

// ── Props-kontrakt ──────────────────────────────────────────────────────────
export interface FlowOrigin {
  sted: string;
  lat: number;
  lon: number;
  belop: number;
}

export interface FlowDestinasjon {
  adresse: string;
  lat: number;
  lon: number;
}

export interface PengestromMapProps {
  origins: FlowOrigin[];
  destinasjon: FlowDestinasjon;
}

// ── Projeksjon (ekvirektangulær m/ standardparallell — ingen lib) ────────────
// Norge ligger på 58–71°N; ren lon→x ville strekke kartet horisontalt. Vi
// korrigerer lengdegrad med cos(standardparallell) slik at omrisset ligner Norge.
const BBOX = norwayGeo.bbox as [number, number, number, number]; // [lonMin, latMin, lonMax, latMax]
const LON_MIN = BBOX[0];
const LAT_MIN = BBOX[1];
const LON_MAX = BBOX[2];
const LAT_MAX = BBOX[3];

const STD_PARALLELL = ((LAT_MIN + LAT_MAX) / 2) * (Math.PI / 180);
const COS0 = Math.cos(STD_PARALLELL);

const PAD = 26;
const INNER_H = 540;
const X_EXTENT = (LON_MAX - LON_MIN) * COS0;
const Y_EXTENT = LAT_MAX - LAT_MIN;
const SCALE = INNER_H / Y_EXTENT;
const INNER_W = X_EXTENT * SCALE;
const VB_W = Math.round(INNER_W + PAD * 2);
const VB_H = Math.round(INNER_H + PAD * 2);

function project(lat: number, lon: number): { x: number; y: number } {
  return {
    x: PAD + (lon - LON_MIN) * COS0 * SCALE,
    y: PAD + (LAT_MAX - lat) * SCALE,
  };
}

// Bygg "M x y L x y … Z" fra GeoJSON-polygonets ytre ring.
function buildOutline(): string {
  const ring = (norwayGeo.features[0].geometry.coordinates as number[][][])[0];
  return (
    ring
      .map(([lon, lat], i) => {
        const { x, y } = project(lat, lon);
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ") + " Z"
  );
}
const OUTLINE = buildOutline();

// ── Komponent ────────────────────────────────────────────────────────────────
export default function PengestromMap({ origins, destinasjon }: PengestromMapProps) {
  const dest = project(destinasjon.lat, destinasjon.lon);
  const totalAmount = origins.reduce((s, o) => s + o.belop, 0);
  const maxBelop = origins.reduce((m, o) => Math.max(m, o.belop), 0) || 1;

  // node-radius skaleres med beløp (mockup: ~10–20 px på 900-bredt viewBox;
  // her er viewBox smalere, så vi holder oss litt mindre).
  const radiusFor = (belop: number) => 5 + (belop / maxBelop) * 11;

  return (
    <div className="surface-dark relative overflow-hidden" style={{ height: 460 }}>
      <style>{`
        @keyframes pengestrom-flow { to { stroke-dashoffset: -90; } }
        .pengestrom-flow-line { animation: pengestrom-flow 3.2s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pengestrom-flow-line { animation: none; }
        }
      `}</style>

      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Pengestrøm fra ${origins.length} investor-steder til ${destinasjon.adresse}`}
      >
        <defs>
          {/* node-glød (golden → sunset → gjennomsiktig), som mockupens .node .glow */}
          <radialGradient id="pm-node" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFB45C" />
            <stop offset="45%" stopColor="#F28B2C" />
            <stop offset="70%" stopColor="#F28B2C" stopOpacity="0" />
          </radialGradient>
          {/* dest-node (hvit kjerne → golden → sunset) */}
          <radialGradient id="pm-dest" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="40%" stopColor="#FFB45C" />
            <stop offset="70%" stopColor="#F28B2C" />
            <stop offset="100%" stopColor="#F28B2C" stopOpacity="0" />
          </radialGradient>
          {/* flyt-linje: gjennomsiktig ved kilde → amber inn mot prosjektet */}
          <linearGradient id="pm-flow" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#F28B2C" stopOpacity="0" />
            <stop offset="100%" stopColor="#FFB45C" />
          </linearGradient>
          {/* mykt glød-blur for haloer */}
          <filter id="pm-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>

        {/* Norge-omriss: svakt amber-fyll over den mørke espresso-flaten */}
        <path
          d={OUTLINE}
          fill="rgba(255,180,90,0.06)"
          stroke="rgba(255,217,163,0.28)"
          strokeWidth={1}
          strokeLinejoin="round"
        />

        {/* buede flyt-linjer fra hver investor-by inn til prosjektet */}
        <g fill="none" stroke="url(#pm-flow)" strokeWidth={1.5} strokeDasharray="4 6">
          {origins.map((o, i) => {
            const p = project(o.lat, o.lon);
            const mx = (p.x + dest.x) / 2;
            const my = (p.y + dest.y) / 2;
            const dx = dest.x - p.x;
            const dy = dest.y - p.y;
            const len = Math.hypot(dx, dy) || 1;
            // kontrollpunkt = midtpunkt forskjøvet vinkelrett → mild bue
            const bow = len * 0.16;
            const cx = mx + (-dy / len) * bow;
            const cy = my + (dx / len) * bow;
            return (
              <path
                key={`flow-${i}`}
                className="pengestrom-flow-line"
                d={`M${p.x.toFixed(1)},${p.y.toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${dest.x.toFixed(1)},${dest.y.toFixed(1)}`}
              />
            );
          })}
        </g>

        {/* investor-noder */}
        {origins.map((o, i) => {
          const p = project(o.lat, o.lon);
          const r = radiusFor(o.belop);
          return (
            <g key={`node-${i}`}>
              <circle cx={p.x} cy={p.y} r={r * 1.7} fill="#F28B2C" opacity={0.35} filter="url(#pm-glow)" />
              <circle cx={p.x} cy={p.y} r={r} fill="url(#pm-node)" />
              <text
                x={p.x}
                y={p.y + r + 12}
                textAnchor="middle"
                fontSize={10.5}
                fill="#FFD9A3"
                style={{ textShadow: "0 1px 4px #000" }}
              >
                {o.sted}
              </text>
              <text
                x={p.x}
                y={p.y + r + 24}
                textAnchor="middle"
                fontSize={9.5}
                fill="#d9a878"
                style={{ textShadow: "0 1px 4px #000" }}
              >
                {formatAmount(o.belop)}
              </text>
            </g>
          );
        })}

        {/* prosjekt-node (destinasjon) */}
        <g>
          <circle cx={dest.x} cy={dest.y} r={34} fill="#FFB45C" opacity={0.4} filter="url(#pm-glow)" />
          <circle cx={dest.x} cy={dest.y} r={16} fill="url(#pm-dest)" />
          {/* Etikett OVER dest-noden — origin-etikettene går ned, så dette unngår
              kollisjon i den tette sør-klyngen (Oslo/Bergen/Kristiansand). */}
          <text
            x={dest.x}
            y={dest.y - 28}
            textAnchor="middle"
            fontSize={11}
            fontWeight={700}
            fill="#ffffff"
            style={{ textShadow: "0 0 6px #000, 0 1px 4px #000" }}
          >
            {destinasjon.adresse}
          </text>
          <text
            x={dest.x}
            y={dest.y - 15}
            textAnchor="middle"
            fontSize={9.5}
            fill="#ffd9a3"
            style={{ textShadow: "0 0 6px #000, 0 1px 4px #000" }}
          >
            prosjekt · {formatAmount(totalAmount)}
          </text>
        </g>
      </svg>

      {origins.length === 0 && (
        <div
          className="tab-num"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#b07a48",
            fontSize: 12,
            textAlign: "center",
            padding: 24,
          }}
        >
          Ingen investor-geografi tilgjengelig ennå.
        </div>
      )}
    </div>
  );
}
